<template>
  <WizardModal
    :show-modal="isVisible"
    :modal-title="modalTitle"
    :steps="wizardSteps"
    size="medium"
    :reset-on-close="true"
    :loading="isSaving || isUploading"
    @cancel="handleClose"
    @finish="handleFinish"
    @close="handleClose"
  >
    <!-- Step 0: Basic Info -->
    <template #step-0>
      <div class="wizard-step-content">
        <!-- Franchise Selection -->
        <div class="form-row">
          <FranchiseSelect
            v-model="formData.franchise_id"
            elem-id="refund-franchise"
            :label="t('Franquia')"
            :disabled="isEditMode"
            @update:modelValue="handleFranchiseChange"
          />
        </div>

        <!-- Title -->
        <div class="form-row">
          <input-text
            v-model="formData.title"
            data-testid="refund-titulo-input"
            elem-id="refund-title"
            :label="t('Título')"
            :placeholder="t('Ex: Combustível para atendimento')"
            :required="true"
            :validation="() => formData.title && formData.title.trim() !== ''"
          />
        </div>

        <!-- Amount -->
        <div class="form-row">
          <InputCurrency
            v-model="formData.amount"
            elem-id="refund-amount"
            :label="t('Valor')"
            ng-required="true"
            :validation="() => formData.amount > 0"
          />
        </div>

        <!-- Expense Date -->
        <div class="form-row">
          <InputDate
            v-model="formData.expense_date"
            elem-id="refund-expense-date"
            :label="t('Data da despesa')"
            :ng-required="true"
            :validation="() => !!formData.expense_date"
          />
        </div>
      </div>
    </template>

    <!-- Step 1: User Selection (pays_to) -->
    <template #step-1>
      <div class="wizard-step-content">
        <h3 class="step-title">{{ t('Selecione quem receberá o reembolso') }}:</h3>

        <div v-if="loadingUsers" class="loading-container">
          <i class="pi pi-spin pi-spinner" style="font-size: 2rem;"></i>
          <p>{{ t('Carregando usuários...') }}</p>
        </div>

        <inline-message v-else-if="userOptions.length === 0" severity="warn">
          {{ t('Nenhum usuário disponível para esta franquia') }}
        </inline-message>

        <ButtonsList
          v-else
          :itens-select="userOptions"
          :value="formData.pays_to"
          @input="formData.pays_to = $event"
          elem-id="refund_pays_to"
          :search-enabled="userOptions.length > 5"
        />
      </div>
    </template>

    <!-- Step 2: Description & Receipt -->
    <template #step-2>
      <div class="wizard-step-content">
        <!-- Description (Rich Text) -->
        <div class="form-row">
          <InputRichText
            v-model="formData.description"
            elem-id="refund-description"
            :label="t('Descrição')"
            :placeholder="t('Descreva o motivo do reembolso (opcional)')"
            :show-toolbar="true"
            min-height="120px"
          />
        </div>

        <!-- File Upload -->
        <div class="form-row">
          <label class="input-label">
            <span class="required-indicator">*</span>
            {{ t('Comprovante') }}
          </label>

          <!-- Show current file if exists -->
          <div v-if="formData.file_url && !newFile" class="current-file">
            <div class="file-preview">
              <i class="pi pi-file"></i>
              <a :href="formData.file_url" target="_blank" class="file-link">
                {{ t('Ver comprovante atual') }}
              </a>
              <AppButton
                data-testid="refund-remover-comprovante-btn"
                icon="pi pi-times"
                severity="danger"
                size="small"
                text
                @click="removeCurrentFile"
                :title="t('Remover comprovante')"
              />
            </div>
          </div>

          <!-- File Input -->
          <FileModel
            v-if="!formData.file_url || newFile"
            data-testid="refund-comprovante-input"
            class="w-full"
            @input="onFileSelect"
          />

          <!-- File Preview -->
          <div v-if="newFile" class="file-preview">
            <i class="pi pi-file"></i>
            <span class="file-name">{{ newFile.name }}</span>
            <span class="file-size">({{ formatFileSize(newFile.size) }})</span>
            <AppButton
              data-testid="refund-remover-novo-arquivo-btn"
              icon="pi pi-times"
              severity="danger"
              size="small"
              text
              @click="removeNewFile"
              :title="t('Remover')"
            />
          </div>

          <!-- Upload Progress -->
          <ProgressBar v-if="isUploading" :value="uploadProgress" class="mt-2" />

          <small class="help-text">
            {{ t('Formatos aceitos: JPG, PNG, PDF. Tamanho máximo: 5MB') }}
          </small>
        </div>

        <!-- Delete Button (only for edit mode with pending status) -->
        <div v-if="isEditMode && refundData?.status === 0" class="delete-section">
          <AppButton
            data-testid="refund-excluir-solicitacao-btn"
            :label="t('Excluir solicitação')"
            icon="pi pi-trash"
            severity="danger"
            size="small"
            @click="handleDelete"
          />
        </div>
      </div>
    </template>
  </WizardModal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../../utils/DebugLogger';
import { eventBus } from '../../../eventBus';
import { usePayrollRefunds } from '../../../composables/usePayrollRefunds';
import { useConfirmAction } from '../../../composables/useConfirmAction';
import { useDateFormatter } from '../../../composables/useDateFormatter';
import { permissionsService } from '../../../services/permissionsService';

type Id = number | string;

interface UserOption {
  label: string;
  value: Id;
  [key: string]: unknown;
}

interface RefundData {
  id?: Id;
  franchise_id?: Id | null;
  pays_to?: Id | null;
  title?: string;
  amount?: number;
  expense_date?: string | Date;
  expense_date_string?: string;
  description?: string;
  blob_name?: string;
  file_url?: string;
  status?: number;
  [key: string]: unknown;
}

interface FormData {
  franchise_id: Id | null;
  pays_to: Id | null;
  title: string;
  amount: number;
  expense_date: string;
  description: string;
  blob_name: string;
  file_url: string;
}

interface UploadResult {
  blob_name?: string;
  file_url?: string;
}

interface WizardStep {
  title: string;
  description: string;
  validate: () => boolean | null | undefined;
}

const { t } = useI18n();
const { createRefund, updateRefund, deleteRefund, getRefund, uploadReceipt, error } = usePayrollRefunds();
const { confirmAction } = useConfirmAction();
const { fromDateToApiDate } = useDateFormatter();

const isVisible = ref<boolean>(false);
const refundId = ref<Id | null>(null);
const refundData = ref<RefundData | null>(null);
const isUploading = ref<boolean>(false);
const isSaving = ref<boolean>(false);
const uploadProgress = ref<number>(0);
const newFile = ref<File | null>(null);
const loadingUsers = ref<boolean>(false);
const userOptions = ref<UserOption[]>([]);

const formData = ref<FormData>({
  franchise_id: null,
  pays_to: null,
  title: '',
  amount: 0,
  expense_date: '',
  description: '',
  blob_name: '',
  file_url: ''
});

const isEditMode = computed((): boolean => {
  return !!refundId.value || !!refundData.value?.id;
});

const modalTitle = computed((): string => {
  return isEditMode.value
    ? t('Editar Solicitação de Reembolso')
    : t('Nova Solicitação de Reembolso');
});

const validateStep0 = (): boolean => {
  return !!(formData.value.franchise_id &&
    formData.value.title &&
    formData.value.title.trim() !== '' &&
    formData.value.amount > 0 &&
    formData.value.expense_date);
};

const validateStep1 = (): boolean => {
  return !!formData.value.pays_to;
};

const validateStep2 = (): boolean => {
  return !!(formData.value.blob_name || newFile.value);
};

const wizardSteps = computed((): WizardStep[] => [
  { title: t('Informações'), description: t('Dados básicos do reembolso'), validate: validateStep0 },
  { title: t('Destinatário'), description: t('Quem receberá o reembolso'), validate: validateStep1 },
  { title: t('Comprovante'), description: t('Descrição e anexo'), validate: validateStep2 }
]);

const resetForm = (): void => {
  formData.value = {
    franchise_id: null,
    pays_to: null,
    title: '',
    amount: 0,
    expense_date: '',
    description: '',
    blob_name: '',
    file_url: ''
  };
  newFile.value = null;
  refundId.value = null;
  refundData.value = null;
  userOptions.value = [];
};

const handleFranchiseChange = (franchiseId: Id | null): void => {
  log('PayrollRefundsModal', 'handleFranchiseChange', 'Franchise changed', franchiseId);
  formData.value.pays_to = null;
  if (franchiseId) {
    loadUsersForFranchise(franchiseId);
  } else {
    userOptions.value = [];
  }
};

const loadUsersForFranchise = (franchiseId: Id): void => {
  loadingUsers.value = true;
  try {
    log('PayrollRefundsModal', 'loadUsersForFranchise', 'Loading users for franchise', franchiseId);
    const operadores = permissionsService.getOperadoresSelectArrayByFranquiaID(Number(franchiseId));
    userOptions.value = operadores as unknown as UserOption[];
    log('PayrollRefundsModal', 'loadUsersForFranchise', 'Loaded users', userOptions.value.length);
  } catch (err) {
    log('PayrollRefundsModal', 'loadUsersForFranchise', 'Error loading users', err);
    userOptions.value = [];
  } finally {
    loadingUsers.value = false;
  }
};

const buildExpenseDateStr = (data: RefundData): string => {
  if (data.expense_date_string) return data.expense_date_string;
  if (data.expense_date instanceof Date) return fromDateToApiDate(data.expense_date);
  if (data.expense_date) return data.expense_date as string;
  return '';
};

const handleModalOpen = async (data: unknown = null): Promise<void> => {
  const refund = data as RefundData | null;
  log('PayrollRefundsModal', 'handleModalOpen', 'Opening modal', refund);
  resetForm();

  if (refund?.id) {
    refundId.value = refund.id;
    refundData.value = refund;
    const hasFullData = refund.title || (refund.franchise_id && refund.amount);

    if (hasFullData) {
      formData.value = {
        franchise_id: refund.franchise_id ?? null,
        pays_to: refund.pays_to ?? null,
        title: refund.title || '',
        amount: refund.amount || 0,
        expense_date: buildExpenseDateStr(refund),
        description: refund.description || '',
        blob_name: refund.blob_name || '',
        file_url: refund.file_url || ''
      };
      if (refund.franchise_id) loadUsersForFranchise(refund.franchise_id);
    } else {
      const fetched = await getRefund(Number(refund.id));
      if (fetched) {
        formData.value = {
          franchise_id: fetched.franchise_id ?? null,
          pays_to: fetched.pays_to ?? null,
          title: fetched.title || '',
          amount: fetched.amount || 0,
          expense_date: buildExpenseDateStr(fetched),
          description: fetched.description || '',
          blob_name: fetched.blob_name || '',
          file_url: fetched.file_url || ''
        };
        if (fetched.franchise_id) loadUsersForFranchise(fetched.franchise_id);
      }
    }
  } else if (refund) {
    formData.value.franchise_id = refund.franchise_id ?? null;
    if (refund.franchise_id) loadUsersForFranchise(refund.franchise_id);
  }

  isVisible.value = true;
};

const handleClose = (): void => {
  log('PayrollRefundsModal', 'handleClose', 'Closing modal');
  isVisible.value = false;
  resetForm();
};

const uploadFile = async (): Promise<UploadResult> => {
  if (!newFile.value) {
    return { blob_name: formData.value.blob_name, file_url: formData.value.file_url };
  }
  isUploading.value = true;
  uploadProgress.value = 0;
  try {
    const result = await uploadReceipt(refundId.value || 0, newFile.value);
    return { blob_name: result.blob_name, file_url: result.file_url };
  } catch (err) {
    log('PayrollRefundsModal', 'uploadFile', 'Error', err);
    throw err;
  } finally {
    isUploading.value = false;
    uploadProgress.value = 0;
  }
};

const handleFinish = async (): Promise<void> => {
  log('PayrollRefundsModal', 'handleFinish', 'Finishing wizard', formData.value);
  isSaving.value = true;
  try {
    let uploadResult: UploadResult = { blob_name: formData.value.blob_name, file_url: formData.value.file_url };
    if (newFile.value) {
      uploadResult = await uploadFile();
      if (!uploadResult?.blob_name) {
        eventBus.emit('globalMessage.error', t('Falha ao fazer upload do comprovante'));
        return;
      }
    }
    const payload = {
      title: formData.value.title,
      description: formData.value.description,
      amount: formData.value.amount,
      expense_date: formData.value.expense_date,
      blob_name: uploadResult.blob_name,
      franchise_id: formData.value.franchise_id,
      pays_to: formData.value.pays_to
    };
    let result: unknown;
    let errorMessage: string | null = null;
    if (isEditMode.value) {
      const id = refundId.value || refundData.value?.id;
      if (id === undefined) throw new Error('Cannot update refund: missing id');
      result = await updateRefund(id, payload);
      if (!result) errorMessage = error.value || t('Erro ao atualizar reembolso');
    } else {
      result = await createRefund(payload);
      if (!result) errorMessage = error.value || t('Erro ao criar solicitação de reembolso');
    }
    if (result) {
      log('PayrollRefundsModal', 'handleFinish', 'Saved successfully', result);
      eventBus.emit('payroll-refunds.created');
      eventBus.emit('payroll-refunds.updated');
      eventBus.emit('payroll-refunds.refresh');
      eventBus.emit('globalMessage.success', isEditMode.value
        ? t('Reembolso atualizado com sucesso')
        : t('Solicitação de reembolso criada com sucesso'));
      handleClose();
    } else {
      log('PayrollRefundsModal', 'handleFinish', 'Error from backend', errorMessage);
      eventBus.emit('globalMessage.error', errorMessage);
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    log('PayrollRefundsModal', 'handleFinish', 'Error', err);
    eventBus.emit('globalMessage.error', message || t('Erro ao salvar reembolso'));
  } finally {
    isSaving.value = false;
  }
};

const handleDelete = (): void => {
  const id = refundId.value || refundData.value?.id;
  if (!id) return;
  log('PayrollRefundsModal', 'handleDelete', 'Deleting refund', id);
  confirmAction({
    message: t('Tem certeza que deseja excluir esta solicitação de reembolso?'),
    header: t('Confirmação'),
    onAccept: async () => {
      const success = await deleteRefund(Number(id));
      if (success) {
        eventBus.emit('globalMessage.success', { message: t('Solicitação de reembolso excluída com sucesso'), duration: 3000 });
        handleClose();
        eventBus.emit('payroll-refunds.refresh');
      } else {
        eventBus.emit('globalMessage.error', { message: t('Erro ao excluir solicitação de reembolso'), duration: 5000 });
      }
    }
  });
};

const onFileSelect = (file: File | null): void => {
  if (file) {
    log('PayrollRefundsModal', 'onFileSelect', 'File selected', { name: file.name, size: file.size });
    newFile.value = file;
  }
};

const removeNewFile = (): void => { newFile.value = null; };

const removeCurrentFile = (): void => {
  formData.value.blob_name = '';
  formData.value.file_url = '';
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const handleOpenNew = (): void => { handleModalOpen(null); };

onMounted(() => {
  eventBus.on('modal.payroll-refunds.open', handleModalOpen);
  eventBus.on('modal.payroll-refunds.close', handleClose);
  eventBus.on('payroll-refunds.open-modal-new', handleOpenNew);
  eventBus.on('payroll-refunds.open-modal-edit', handleModalOpen);
  eventBus.on('payroll-refunds.edit', handleModalOpen);
});

onBeforeUnmount(() => {
  eventBus.off('modal.payroll-refunds.open', handleModalOpen);
  eventBus.off('modal.payroll-refunds.close', handleClose);
  eventBus.off('payroll-refunds.open-modal-new', handleOpenNew);
  eventBus.off('payroll-refunds.open-modal-edit', handleModalOpen);
  eventBus.off('payroll-refunds.edit', handleModalOpen);
});
</script>

<style scoped>
.wizard-step-content {
  padding: 0.5rem;
}

.step-title {
  margin-bottom: 1rem;
  font-weight: 500;
  color: var(--text-color);
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  gap: 1rem;
}

.form-row {
  margin-bottom: 1.25rem;
}

.input-label {
  display: block;
  font-weight: 500;
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
  color: var(--text-color);
}

.required-indicator {
  color: var(--red-500);
  margin-right: 0.25rem;
}

.current-file {
  margin-bottom: 0.5rem;
}

.file-preview {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: var(--surface-50);
  border: 1px solid var(--surface-200);
  border-radius: 6px;
  margin-bottom: 0.5rem;
}

.file-preview i {
  color: var(--primary-color);
}

.file-link {
  color: var(--primary-color);
  text-decoration: none;
}

.file-link:hover {
  text-decoration: underline;
}

.file-name {
  color: var(--text-color);
  font-weight: 500;
}

.file-size {
  color: var(--text-color-secondary);
  font-size: 0.875rem;
}

.help-text {
  color: var(--text-color-secondary);
  font-size: 0.75rem;
  margin-top: 0.5rem;
  display: block;
}

.delete-section {
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid var(--surface-200);
  display: flex;
  justify-content: flex-end;
}

.w-full {
  width: 100%;
}

.mt-2 {
  margin-top: 0.5rem;
}
</style>
